import { useCallback, useMemo } from "react";
import {
  LastMessageFragment,
  MessageFragment,
  MessageThreadFragment,
  MessageType,
  QuotedMessage,
} from "gql/graphql";
import { useAuth } from "modules/providers/auth-provider";
import useUserPreference from "modules/hooks/use-user-preference";
import useAppState from "./use-app-state";
import { getLanguageName } from "modules/utils/translation";

const useMessageThreadTranslation = (
  message: MessageFragment | LastMessageFragment,
  messageThread?: MessageThreadFragment,
) => {
  const { me } = useAuth();
  const { stringValue: locale } = useUserPreference({
    userId: me?.id,
    name: "LOCALE",
  });
  const { arrayValue, toggleArrayValue } = useAppState({
    appStateType: "originalTextMessageIdList",
  });

  const isMessageThreadTranslated = messageThread && messageThread.locale;

  const isMessageTranslated = useMemo(() => {
    if (
      locale &&
      isMessageThreadTranslated &&
      locale !== message.locale &&
      me?.id !== message.createdUserId &&
      message.type === MessageType.User &&
      message.localizedText
    ) {
      return true;
    }
    return false;
  }, [
    locale,
    isMessageThreadTranslated,
    message.locale,
    message.createdUserId,
    message.type,
    message.localizedText,
    me?.id,
  ]);

  const isViewingTranslatedMessage = useMemo(
    () => isMessageTranslated && !arrayValue.includes(message.id),
    [arrayValue, isMessageTranslated, message.id],
  );

  const originalMessageLanguage = useMemo(
    () => getLanguageName(message.locale),
    [message.locale],
  );

  const isViewingTranslatedQuotedMessage = useCallback(
    (quotedMessage: QuotedMessage) => {
      if (
        locale &&
        isMessageThreadTranslated &&
        locale !== quotedMessage.locale &&
        me?.id !== quotedMessage.createdUserId
      ) {
        return true;
      }
      return false;
    },
    [locale, me?.id, isMessageThreadTranslated],
  );

  const toggleViewOriginalMessage = useCallback(() => {
    toggleArrayValue(message.id);
  }, [message.id, toggleArrayValue]);

  return {
    isMessageThreadTranslated,
    isMessageTranslated,
    isViewingTranslatedMessage,
    originalMessageLanguage,
    toggleViewOriginalMessage,
    isViewingTranslatedQuotedMessage,
  };
};

export default useMessageThreadTranslation;
